<template>
  <v-container class="pa-0">
    <h3 class="mb-3">Add a Resource</h3>
    <hr class="thick" />
    <div class="resourceBox pa-2 mb-2">
      <p><strong>Select the type of resource you would like to add:</strong></p>
      <v-radio-group v-model="type" class="pt-0">
        <v-radio label="Upload File(s)" value="upload"></v-radio>
        <v-radio label="Add URL" value="url"></v-radio>
        <!-- <v-radio label="Select from Education Services Resources" value="es"></v-radio> -->
      </v-radio-group>
    </div>
    <div v-if="type === 'upload'">
      <v-form ref="uploadForm" v-model="uploadForm.valid">
        <amaze-editor
          v-model="description"
          :rules="uploadForm.description"
          label="Add a comment about the resource*:"
        ></amaze-editor>
        <p class="mb-0"><strong>Upload one or more files*:</strong></p>
        <small class="mt-0">Maximum file size: 100MB</small>
        <amaze-upload
          dropzone-id="resource-upload"
          @new-userfile-id="updateUserfileId"
          @dropzone-file-removed="removeUserfileId"
          class="mb-3"
        ></amaze-upload>
        <v-btn
          color="primary"
          @click="createNewCard()"
          class="ml-0"
          :disabled="submitting || !uploadFormValid"
          >Add Resource</v-btn
        >
        <v-btn @click="$router.go(-1)" flat outline>Cancel and Close</v-btn>
      </v-form>
    </div>
    <div v-if="type === 'url'">
      <v-form ref="urlForm" v-model="urlForm.valid">
        <amaze-editor
          v-model="description"
          label="Add a comment about the resource*"
          :rules="urlForm.description"
        ></amaze-editor>
        <v-text-field
          v-model="url"
          outline
          label="Enter the URL (including http:// or https://, example: https://google.com)"
          :rules="urlForm.url"
        ></v-text-field>
        <v-text-field
          v-model="friendly_text"
          outline
          label="Enter Display Text (example: Google)"
          :rules="urlForm.friendly_text"
          class="mb-0"
        ></v-text-field>
        <v-btn
          color="primary"
          @click="createNewCard()"
          class="ml-0"
          :disabled="submitting || !uploadFormValid"
          >Add Resource</v-btn
        >
        <v-btn @click="returnToGroup()" flat outline>Cancel and Close</v-btn>
      </v-form>
    </div>

    <div v-if="type === 'es'">
      <v-form ref="esForm" v-model="esForm.valid">
        <v-textarea
          outline
          v-model="description"
          label="Add a comment about the resource*"
          placeholder="Type your comment here."
          :rules="esForm.description"
        ></v-textarea>
        <p><strong>Select a resource from the list below:</strong></p>
        <hr />
        <template v-for="resource in esResources">
          <amaze-userfile :userfile="resource" :key="resource.id">
            <v-btn
              small
              class="ml-0 mt-2"
              color="primary"
              @click="addESResource(resource.id)"
              >Select Resource</v-btn
            >
          </amaze-userfile>
        </template>
        <hr />
        <v-btn class="ml-0 mt-3" @click="returnToGroup()" flat outline
          >Cancel and Close</v-btn
        >
      </v-form>
    </div>
  </v-container>
</template>

<style lang="scss" scoped>
@import "src/styles/variables.scss";
.resourceBox {
  background-color: $amaze-yellow-background;
}
::v-deep form {
   .v-input__slot {
     label {
        font-size: 15px;
        line-height: 1.5em;
        font-weight: bolder;
     }
   }
 }
</style>

<script>
import { EventBus } from "@/components/Events/event-bus.js";
import cardMixin from "@/mixins/cardMixin.js";
import cardCrudMixin from "@/mixins/cardCrudMixin.js"

export default {
  mixins: [cardMixin, cardCrudMixin],
  data() {
    return {
      type: "",
      description: "",
      userfileId: [],
      url: null,
      esResources: [],
      friendly_text: null,
      submitting: false,
      uploadForm: {
        valid: false,
        description: [
          v => !!v || "Please enter a description for the resource",
          v =>
            (v && (v || "").length < 65535) ||
            "Please limit your description to 65535 characters or less."
        ],
        userfileId: [
          v => !!v || "There appears to be no file attached. Please try again."
        ]
      },
      urlForm: {
        valid: false,
        description: [
          v => !!v || "Please enter a description for the resource",
          v =>
            (v && (v || "").length < 65535) ||
            "Please limit your description to 65535 characters or less."
        ],
        url: [v => !!v || "Please enter a URL"],
        friendly_text: [
          v =>
            !!v ||
            "Please enter the text you'd like displayed on Amaze for this link."
        ]
      },
      esForm: {
        valid: false,
        description: [
          v => !!v || "Please enter a description for the resource",
          v =>
            (v && (v || "").length < 65535) ||
            "Please limit your description to 65535 characters or less."
        ]
      }
    };
  },
  computed: {
    summary: function () {
      return this.createSummary(this.description) || "";
    },
    uploadFormValid() {
      return this.type !== "upload" || (this.uploadForm.valid && this.userfileId.length > 0);
    },
    fileURL() {
      return process.env.VUE_APP_FILE_BASE;
    }
  },
  methods: {
    createNewCard() {
      if (
        (this.type === "upload" &&
          this.$refs.uploadForm.validate() &&
          this.userfileId.length > 0) ||
        (this.type === "url" && this.$refs.urlForm.validate()) ||
        (this.type === "es" && this.$refs.esForm.validate())
      ) {
        const groupId = this.$route.params.id;
        let newCard = {
          name: "My Great Card",
          card_type_id: 2,
          user_id: this.$auth.user().id,
          allow_comments: 1,
          allow_likes: 1,
          groups: [groupId],
          summary: this.createSummary(this.description)
        };
        if (this.userfileId.length > 0) {
          newCard.userfile_id = this.userfileId;
          newCard.description = this.description;
        }
        if (this.url) {
          newCard.url = this.url;
          newCard.friendly_text = this.friendly_text;
          newCard.description = this.description;
        }
        this.submitting = true;
        this.$http
          .post("cards", newCard)
          .then(
            response => {
              EventBus.$emit("refresh-cards", groupId, true);
              this.returnToGroup();
            },
            response => {
              console.log("Error creating card.");
            }
          )
          .finally(() => {
            this.submitting = false;
          });
      } else {
        let message =
          "Nice try, but some required information is missing. Give it another go!";
        if (this.type === "upload" && this.userfileId.length === 0) {
          message = "Please attach at least one file.";
        }
        this.$store.commit("updateMessage", {
          message: message,
          snackbar: true
        });
      }
    },
    updateUserfileId(userfileId) {
      if (typeof userfileId.id !== "undefined") {
        this.userfileId.push(userfileId.id);
      } else {
        this.userfileId.push(userfileId);
      }
    },
    removeUserfileId(userfileId) {
      let idx = this.userfileId.indexOf(userfileId);
      if (idx > -1) {
        this.userfileId.splice(idx, 1);
      }
    },
    getESFiles() {
      this.$http.get("userfiles/es_approved").then(
        response => {
          this.esResources = response.data.data;
        },
        response => {
          console.log("Error getting ES resources.");
        }
      );
    },
    addESResource(userfileId) {
      this.updateUserfileId(userfileId);
      this.createNewCard();
    }
  },
  mounted() {
    const returnRoute = "/group/" + this.$route.params.id;
    this.$emit('setReturnRoute', returnRoute);
    this.getESFiles();
  }
};
</script>
